import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { Criteria, CriteriaPurpose, Granularity, Policy, Purpose } from '../../types';
import { HighlightedText } from '../common/highlightText';
import {
  countHits,
  currentPolicy,
  matchingPolicyLines,
  policiesForCriterion,
  policyVersionOptionLabel,
  purposesForCriterion,
  rankedByHits,
  SearchScope,
} from './criteriaPolicySearch';

export type GranularityFilter = 'all' | Granularity;

const GRANULARITY_FILTERS: { value: GranularityFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sentence', label: 'Sentence' },
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'essay', label: 'Essay' },
];

export type ArchiveFilter = 'active' | 'archived' | 'all';

const ARCHIVE_FILTERS: { value: ArchiveFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];

const NamedCountChip = ({
  items,
  singular,
  plural,
}: {
  items: string[];
  singular: string;
  plural: string;
}) => {
  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  const label = items.length === 1 ? `1 ${singular}` : `${items.length} ${plural}`;

  return (
    <Tooltip
      title={
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {items.map(item => (
            <Box component="li" key={item}>
              {item}
            </Box>
          ))}
        </Box>
      }
    >
      <Chip size="small" variant="outlined" label={label} />
    </Tooltip>
  );
};

const PolicyVersionChip = ({
  current,
  versions,
}: {
  current: Policy | undefined;
  versions: Policy[];
}) => {
  if (!current && versions.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  const label = current ? (current.version != null ? `v${current.version}` : 'unversioned') : '—';

  return (
    <Tooltip
      title={
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {versions.map(version => (
            <Box component="li" key={version.id}>
              {policyVersionOptionLabel(version, version.id === current?.id)}
            </Box>
          ))}
        </Box>
      }
    >
      <Chip size="small" variant="outlined" label={label} />
    </Tooltip>
  );
};

const CriteriaPolicyList = ({
  criteria,
  allCriteriaCount,
  policies,
  allPurposes,
  criteriaPurposes,
  searchQuery,
  onSearchQueryChange,
  searchScope,
  onSearchScopeChange,
  archiveFilter,
  onArchiveFilterChange,
  granularityFilter,
  onGranularityFilterChange,
  onEdit,
  onBrowseHistory,
  onAdd,
}: {
  criteria: Criteria[];
  allCriteriaCount: number;
  policies: Policy[];
  allPurposes: Purpose[];
  criteriaPurposes: CriteriaPurpose[];
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  searchScope: SearchScope;
  onSearchScopeChange: (value: SearchScope) => void;
  archiveFilter: ArchiveFilter;
  onArchiveFilterChange: (value: ArchiveFilter) => void;
  granularityFilter: GranularityFilter;
  onGranularityFilterChange: (value: GranularityFilter) => void;
  onEdit: (criterion: Criteria) => void;
  onBrowseHistory: (criterion: Criteria) => void;
  onAdd: () => void;
}) => {
  const needle = searchQuery.trim();
  const searchingPolicies = Boolean(needle) && searchScope !== 'names';
  const ranked = rankedByHits(criteria, policies, searchQuery, searchScope);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const [fullPolicyIds, setFullPolicyIds] = useState<Record<string, boolean>>({});

  const totalHits = ranked.reduce((sum, criterion) => {
    const text = currentPolicy(criterion, policies)?.text ?? '';
    return sum + countHits(text, needle);
  }, 0);

  // Search defaults open so hits are visible; idle defaults closed. Explicit
  // toggles (and Collapse/Expand all) are stored and win over the default.
  const isExpanded = (criterionId: string) =>
    Object.prototype.hasOwnProperty.call(expandedIds, criterionId)
      ? expandedIds[criterionId]
      : Boolean(searchingPolicies);

  const setExpanded = (ids: string[], expanded: boolean) => {
    setExpandedIds(current => ({
      ...current,
      ...Object.fromEntries(ids.map(id => [id, expanded])),
    }));
  };

  const toggleExpanded = (criterionId: string) => {
    setExpanded([criterionId], !isExpanded(criterionId));
  };

  const rankedIds = ranked.map(criterion => criterion.id);
  const anyExpanded = rankedIds.some(id => isExpanded(id));
  const anyCollapsed = rankedIds.some(id => !isExpanded(id));

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h4" component="h1">
          Criteria Management
        </Typography>
        <Button variant="contained" color="primary" onClick={onAdd}>
          Add New Criterion
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          value={searchQuery}
          onChange={event => onSearchQueryChange(event.target.value)}
          placeholder={searchScope === 'names' ? 'Which criteria…' : 'Which policies mention…'}
          inputProps={{ 'aria-label': 'Search criteria and current policies' }}
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => onSearchQueryChange('')}
                  edge="end"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={searchScope}
          onChange={(_event, value: SearchScope | null) => {
            if (value) onSearchScopeChange(value);
          }}
          aria-label="Search in"
        >
          <ToggleButton value="names">Names</ToggleButton>
          <ToggleButton value="policies">Policies</ToggleButton>
          <ToggleButton value="both">Both</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={archiveFilter}
          onChange={(_event, value: ArchiveFilter | null) => {
            if (value) onArchiveFilterChange(value);
          }}
          aria-label="Archive status"
        >
          {ARCHIVE_FILTERS.map(option => (
            <ToggleButton key={option.value} value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={granularityFilter}
          onChange={(_event, value: GranularityFilter | null) => {
            if (value) onGranularityFilterChange(value);
          }}
          aria-label="Granularity"
        >
          {GRANULARITY_FILTERS.map(option => (
            <ToggleButton key={option.value} value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {needle
            ? searchingPolicies
              ? `${totalHits} hits in ${ranked.length} of ${allCriteriaCount} criteria`
              : `${ranked.length} of ${allCriteriaCount} criteria`
            : `${allCriteriaCount} criteria`}
        </Typography>
        {ranked.length > 0 ? (
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <Button
              size="small"
              disabled={!anyCollapsed}
              onClick={() => setExpanded(rankedIds, true)}
            >
              Expand all
            </Button>
            <Button
              size="small"
              disabled={!anyExpanded}
              onClick={() => setExpanded(rankedIds, false)}
            >
              Collapse all
            </Button>
          </Box>
        ) : null}
      </Box>

      {ranked.length === 0 ? (
        <Typography color="text.secondary">No matching criteria</Typography>
      ) : (
        ranked.map(criterion => {
          const policy = currentPolicy(criterion, policies);
          const text = policy?.text ?? '';
          const hits = countHits(text, needle);
          const purposes = purposesForCriterion(criterion.id, criteriaPurposes, allPurposes);
          const versions = policiesForCriterion(policies, criterion.id);
          const expanded = isExpanded(criterion.id);
          const showFullPolicy = !!fullPolicyIds[criterion.id];
          const hitLines = searchingPolicies ? matchingPolicyLines(text, needle) : [];
          const hasRestOfPolicy =
            searchingPolicies && hits > 0 && hitLines.join('\n') !== text.replace(/\n+$/, '');

          return (
            <Paper key={criterion.id} variant="outlined" sx={{ mb: 1.5, overflow: 'hidden' }}>
              <Box
                sx={{
                  px: 1,
                  py: 1,
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center',
                  bgcolor: needle ? 'grey.50' : 'transparent',
                }}
              >
                <IconButton
                  size="small"
                  aria-label={expanded ? `Collapse ${criterion.name}` : `Expand ${criterion.name}`}
                  aria-expanded={expanded}
                  onClick={() => toggleExpanded(criterion.id)}
                >
                  {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
                <Box
                  sx={{ minWidth: 0, flex: 1, cursor: 'pointer' }}
                  onClick={() => toggleExpanded(criterion.id)}
                >
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {needle ? (
                      <HighlightedText text={criterion.name} query={needle} />
                    ) : (
                      criterion.name
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    <Chip size="small" label={criterion.granularity} />
                    {criterion.isArchived ? (
                      <Chip size="small" variant="outlined" label="Archived" />
                    ) : null}
                    <NamedCountChip
                      items={purposes.map(purpose => purpose.name)}
                      singular="purpose"
                      plural="purposes"
                    />
                    <PolicyVersionChip current={policy} versions={versions} />
                    {searchingPolicies && hits > 0 ? (
                      <Chip size="small" color="warning" label={`${hits} hits`} />
                    ) : null}
                  </Box>
                </Box>
                <Tooltip title={policy ? 'Browse Policy History' : 'No current Policy'}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Browse Policy History"
                      disabled={!policy}
                      onClick={event => {
                        event.stopPropagation();
                        onBrowseHistory(criterion);
                      }}
                    >
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Edit">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Edit"
                      onClick={event => {
                        event.stopPropagation();
                        onEdit(criterion);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {expanded ? (
                <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                  {!policy ? (
                    <Typography color="text.disabled">No current Policy</Typography>
                  ) : searchingPolicies ? (
                    <>
                      {showFullPolicy ? (
                        <Typography
                          component="div"
                          sx={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.7 }}
                        >
                          <HighlightedText text={text} query={needle} />
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {hitLines.map((line, index) => (
                            <Typography
                              key={index}
                              variant="body2"
                              sx={{ fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
                            >
                              <HighlightedText text={line} query={needle} />
                            </Typography>
                          ))}
                          {hits === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              Matched on the criterion name.
                            </Typography>
                          ) : null}
                        </Box>
                      )}
                      {hasRestOfPolicy ? (
                        <Button
                          size="small"
                          sx={{ mt: 1, px: 0 }}
                          onClick={() =>
                            setFullPolicyIds(current => ({
                              ...current,
                              [criterion.id]: !showFullPolicy,
                            }))
                          }
                        >
                          {showFullPolicy ? 'Show matching lines' : 'Show full policy'}
                        </Button>
                      ) : null}
                    </>
                  ) : (
                    <Box sx={{ '& p': { mb: 1, mt: 0 }, fontSize: 14, lineHeight: 1.65 }}>
                      <ReactMarkdown remarkPlugins={[remarkBreaks]}>{text}</ReactMarkdown>
                    </Box>
                  )}
                </Box>
              ) : null}
            </Paper>
          );
        })
      )}
    </Container>
  );
};

export default CriteriaPolicyList;
